import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { selectionSet, type AccessControlSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/chemistry/elements/components/x-loading-state';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOperation } from '@adornis/data-view/data-view-operation.js';
import { ExportDataView, exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import '@adornis/filter/client/x-filter-editor';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import '@adornis/fonts/fonts.js';
import { getUserConfigForKey } from '@ils/config/api/getUserConfig.js';
import { DataViewConfig } from '@ils/config/db/data-view-config.js';
import { getRatingExportLinesByFilter } from '@ils/export/api/ratings-export.js';
import { getUserExportLinesByFilter } from '@ils/export/api/user-export.js';
import { RatingsExportLine } from '@ils/export/db/ratings-export.js';
import { RatingsExportDataView } from '@ils/export/db/RatingsExportDataView.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import type { ExamSlotFinalRatingDoc } from '@ils/ratings/db/finalRating.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { ILSUserOverviewLine } from '@legacy-db/ils-user-overview-line.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { switchMap } from 'rxjs';
import { subscribeSearchUser } from '../../../src/api/user.js';
import type { ExamDay } from '../../../src/db/examdays.js';
import { ILSUser } from '../../../src/db/ils-user.js';
import type { Order } from '../../../src/db/order.js';
import { ILSUserOverviewLineDataView } from '../db/ILSUserOverviewLineDataView.js';
import { UserExportLine } from '../db/user-export.js';
import { UserExportDataView } from '../db/UserExportDataView.js';

enum ExportViewpoint {
  Order = 'Bestellung',
  User = 'Nutzer',
  Rating = 'Bewertung',
}
//#region Whitelists
const userWhitelist = {
  _id: 1,
  username: 1,
  email: 1,
  isOnline: 1,
  createdAt: 1,
  emailConfirmed: 1,
  ecommerce: {
    firstName: 1,
    lastName: 1,
    street: 1,
    zip: 1,
    city: 1,
    country: 1,
    phone: 1,
    acceptedLegal: 1,
  },
  coInvoice: 1,
  profile: {
    birthCountry: 1,
    birthplace: 1,
    dateOfBirth: 1,
    gender: 1,
    targetFederalState: 1,
    hasParticipatedExam: 1,
    participatedC1Course: 1,
    wantsNewsletter: 1,
  },
} satisfies AccessControlSelectionSet<ILSUser> as any;

const orderWhitelist = {
  _id: 1,
  status: 1,
  createdAt: 1,
  paymentType: 1,
  amount: 1,
  tax: 1,
  processorInfo: {
    stripePaymentIntent: 1,
  },
  revokedAt: 1,
  invoiceNumber: 1,
  orderNumber: 1,
  comment: 1,
} satisfies AccessControlSelectionSet<Order> as any;

const examDayWhitelist = {
  _id: 1,
  name: 1,
  description: 1,
  published: 1,
  isPublishedForTesters: 1,
  start: 1,
  end: 1,
  activeReservationsCount: 1,
  slotPause: 1,
  breakAfterRounds: 1,
  breakInMinutes: 1,
  ratingType: 1,
  location: {
    displayName: 1,
    address: {
      streetAndHousenumber: 1,
      zipCity: {
        zip: 1,
        city: 1,
      },
      bundesland: 1,
      country: 1,
      additionalInfo: 1,
    },
  },
} satisfies AccessControlSelectionSet<ExamDay> as any;

const slotWhitelist = {
  _id: 1,
  startTime: 1,
} satisfies AccessControlSelectionSet<Slot> as any;

const ratingDocWhitelist = {
  _id: 1,
  _class: 1,
  committed: 1,
  archived: 1,
  ratings: {
    score: 1,
    notes: 1,
  },
  createdAt: 1,
  updatedAt: 1,
  doctorCommitted: 1,
  linguistCommitted: 1,
} satisfies AccessControlSelectionSet<ExamSlotFinalRatingDoc> as any;

const OSCEWhitelist = {
  _id: 1,
  createdAt: 1,
  title: 1,
  titleTrainee: 1,
  osceType: 1,
  ratingConfigName: 1,
  durationInMinutes: 1,
  enableVideo: 1,
  taskType: 1,
  conversationType: 1,
} satisfies AccessControlSelectionSet<OSCE> as any;

//#endregion

const dataviews = {
  [ExportViewpoint.Order]: {
    entityClass: UserExportLine,
    exportClass: UserExportDataView,
    operation: getUserExportLinesByFilter,
    tabName: 'Bestellung',
    configName: 'user-export-line',
    customColumns: UserExportDataView.basicColumnDefinitions,
    whitelist: {
      user: userWhitelist,
      order: orderWhitelist,
      examday: examDayWhitelist,
      passedExamDay: 1,
      userScore: 1,
      isRated: 1,
    } satisfies AccessControlSelectionSet<UserExportLine> as any,
  },
  [ExportViewpoint.User]: {
    entityClass: ILSUserOverviewLine,
    exportClass: ILSUserOverviewLineDataView,
    operation: subscribeSearchUser,
    tabName: 'Nutzer',
    configName: 'user-basic-data',
    customColumns: ILSUserOverviewLineDataView.basicColumnDefinitions,
    whitelist: {
      user: userWhitelist,
    } satisfies AccessControlSelectionSet<ILSUserOverviewLine> as any,
  },
  [ExportViewpoint.Rating]: {
    entityClass: RatingsExportLine,
    exportClass: RatingsExportDataView,
    operation: getRatingExportLinesByFilter,
    tabName: 'Bewertung',
    configName: 'rating-export',
    customColumns: RatingsExportDataView.basicColumnDefinitions,
    whitelist: {
      ratingDoc: ratingDocWhitelist,
      slot: slotWhitelist,
      osce: OSCEWhitelist,
      trainee: userWhitelist,
      inspectors: userWhitelist,
    } satisfies AccessControlSelectionSet<RatingsExportLine> as any,
    // blacklist: {},
  },
} as const satisfies Record<
  ExportViewpoint,
  {
    entityClass: typeof AdornisEntity;
    exportClass: typeof ExportDataView;
    operation: DataViewOperation<AdornisEntity, any>;
    tabName: string;
    configName: string;
    whitelist?: any;
    customColumns: ColumnDefinition<any>[];
  }
>;

/**
 * @element ils-export
 */
@customElement('ils-export')
export class IlsExport extends ChemistryLitElement {
  @state() private readonly tab = new RXController(this, ExportViewpoint.Order);

  @state() private readonly _dataviewConfig = new RXController(
    this,
    this.tab.observable,
    obs =>
      obs.pipe(
        switchMap(tab =>
          getUserConfigForKey(DataViewConfig, dataviews[tab].configName)(selectionSet(() => DataViewConfig, 4)),
        ),
      ),
    { pipeIsLoading: true },
  );

  override render() {
    return html`
      <x-flex space="sm">
        <x-tabs .selected=${Object.values(ExportViewpoint).indexOf(this.tab.value)}>
          ${repeat(
            Object.values(ExportViewpoint),
            viewpoint => viewpoint,
            viewpoint =>
              html` <x-tab @click=${() => this.tab.next(viewpoint)}> ${dataviews[viewpoint].tabName} </x-tab> `,
          )}
        </x-tabs>
        ${this.renderDataView()}
      </x-flex>
    `;
  }

  private renderDataView(): Renderable {
    if (this._dataviewConfig.isLoading) return html`<x-loading-state> </x-loading-state>`;
    const { dataViewOptions, filterOptions } = this.buildDataviewOptions();
    const dataviewDefinition = dataviews[this.tab.value];
    return html`
      <x-flex>
        <x-flex space="md" horizontal crossaxis-start space-between ${css({ padding: `0 ${this.spacing.md}` })}>
          <x-filter-editor flex .options=${filterOptions}></x-filter-editor>

          <x-button
            mode="outline"
            icon="download"
            @click=${() => exportDataView(dataViewOptions, { exportClass: dataviewDefinition.exportClass })}
          >
            Exportieren
          </x-button>
        </x-flex>
        <x-data-view .options=${dataViewOptions}></x-data-view>
      </x-flex>
    `;
  }

  private buildDataviewOptions() {
    const dataviewConfig = this._dataviewConfig.value;
    if (!dataviewConfig) throw new Error('wait for the dataview config to be loaded!');
    const dataviewDefinition = dataviews[this.tab.value];
    const fieldSelectionOptions = {
      class: dataviewDefinition.entityClass,
      shouldIncludeField: dataviewDefinition.whitelist
        ? shouldIncludeFieldHelpers.bySelectionSet({ whitelist: dataviewDefinition.whitelist })
        : () => true,
    };
    const partialDataViewOptions: DataViewOptions<typeof AdornisEntity> = {
      class: dataviewDefinition.entityClass,
      operation: dataviewDefinition.operation,
      isColumnSelectable: true,
      isSortable: true,
      isColumnResortable: true,
      isColumnResizable: true,
      fieldSelectionOptions,
      loader: () => this.renderDataViewLoader(),
      view: dataviewConfig.view,
      customColumns: dataviewDefinition.customColumns,
      onViewChanged: async view => {
        dataviewConfig.view = view;
        if (dataviewConfig._id) await dataviewConfig.save();
        else await dataviewConfig.create();
      },
    };
    const partialFilterOptions: Omit<FilterEditorOptions<typeof AdornisEntity>, 'class' | 'filter'> = {
      fieldAspectGeneration: fieldSelectionOptions,
      onFilterChanged: async filter => {
        if (!dataviewConfig.view) return;
        dataviewConfig.view.filter = filter;
        if (dataviewConfig._id) await dataviewConfig.save();
        else await dataviewConfig.create();
      },
    };
    const options = buildFullOptions(partialDataViewOptions, partialFilterOptions);
    return options;
  }

  private renderDataViewLoader() {
    return html`
      <x-flex flex center crossaxis-center padding="lg" space="lg">
        <x-loader></x-loader>
        <x-text ${css({ textAlign: 'center', maxWidth: this.sizes.maxReadableWidth })}>
          Das erste Laden kann einige Sekunden in Anspruch nehmen, da die aktuellsten Daten neu aggregiert werden
          müssen!
        </x-text>
      </x-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': { display: 'block', background: this.colors.white },
      },
    ];
  }
}
